import type { FC, ReactNode, SVGProps } from 'react'

import { useSvgId } from '@/hooks/use-svg-id'
import { BrandGradient } from '@/components/generals/divi-logo'
import { BRAND_COLORS, WORDMARK } from '@/constants/brand'

type SvgProps = Omit<SVGProps<SVGSVGElement>, 'children' | 'viewBox'>

interface ArtworkProps extends SvgProps {
    label?: string
}

export const NeonWordmark: FC<ArtworkProps> = ({
    label = "Divi neon outline logo",
    ...props
}): ReactNode => {
    const glowId = useSvgId('divi-neon')

    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='-26 -26 388.6 202'
            role='img'
            aria-label={label}
            {...props}
        >
            <defs>
                <filter
                    id={glowId}
                    x='-30%'
                    y='-50%'
                    width='160%'
                    height='200%'
                    colorInterpolationFilters='sRGB'
                >
                    <feGaussianBlur in='SourceGraphic' stdDeviation='8' result='halo' />
                    <feGaussianBlur in='SourceGraphic' stdDeviation='2.4' result='bloom' />
                    <feMerge>
                        <feMergeNode in='halo' />
                        <feMergeNode in='bloom' />
                        <feMergeNode in='SourceGraphic' />
                    </feMerge>
                </filter>
            </defs>

            <g transform={WORDMARK.transform}>
                <path
                    d={WORDMARK.path}
                    fill='none'
                    stroke={BRAND_COLORS.red}
                    strokeWidth='4.5'
                    strokeLinejoin='round'
                    filter={`url(#${glowId})`}
                />
                <path
                    d={WORDMARK.path}
                    fill='none'
                    stroke='#FF8F9D'
                    strokeWidth='1.4'
                    strokeLinejoin='round'
                />
            </g>
        </svg>
    )
}

export const BadgeWordmark: FC<ArtworkProps> = ({
    label = "Divi knockout badge logo",
    ...props
}): ReactNode => {
    const fillId = useSvgId('divi-badge')
    const maskId = useSvgId('divi-knockout')

    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='-52 -36 450 222'
            role='img'
            aria-label={label}
            {...props}
        >
            <defs>
                <BrandGradient id={fillId} x1={-44} x2={358} />

                <mask
                    id={maskId}
                    maskUnits='userSpaceOnUse'
                    x='-60'
                    y='-50'
                    width='440'
                    height='250'
                >
                    <rect x='-60' y='-50' width='440' height='250' fill='#FFFFFF' />
                    <path d={WORDMARK.path} fill='#000000' fillRule='evenodd' />
                </mask>
            </defs>

            <g transform={WORDMARK.transform}>
                <rect
                    x='-44'
                    y='-36'
                    width='402'
                    height='222'
                    rx='30'
                    fill={`url(#${fillId})`}
                    mask={`url(#${maskId})`}
                />
            </g>
        </svg>
    )
}
